import moment from "moment-timezone";
import _ from "lodash";

import BANK_TRANSACTION_ACCOUNTING_LINES_QUERY from "../graphql/accountingItem/BankTransactionAccountingLines.gql";
import DOCUMENT_STATIC_QUERY from "../graphql/accountingItem/DocumentStatic.gql";
import EXPENSE_REPORT_STATIC_QUERY from "../graphql/accountingItem/ExpenseReportStatic.gql";
import ZTILL_RECEIPT_STATIC_QUERY from "../graphql/accountingItem/ZTillReceiptStatic.gql";
import DELETE_DOCUMENT from "../graphql/document/DeleteDocument.gql";

import { DialogAction } from "@/services/dialogAction";
import { isNotSubjectToVAT } from "@/services/vat/vatHelpers.services";
import { Component, Prop } from "vue-facing-decorator";
import { documentLogs } from "../services/accountingItemServices";
import {
    getAccountingDate,
    getLastClosedAndFirstOpenAccountingPeriods,
} from "../services/accountingPeriods";
import { computeNotJustifiedAmountSigned } from "../services/bankTransactionServices";
import { mathFix } from "../services/mathServices";
import { getReconciliationCallToActionTitle } from "../services/reconciliationServices";
import { getUserDisplayName } from "../services/userServices";
import { User } from "../store/store";
import { ComponentBase } from "./ComponentBase";
import { showThirdPartyCompanyOrBankAccountOrUser } from "../services/accountableAccountServices"

@Component({
    emits: ["openReconciliation", "refresh"],
})
export class AccountingItemBase extends ComponentBase {
    @Prop({ default: "Unknown" }) className: string;
    @Prop({ default: "Router" }) mode: string;
    @Prop({ default: "" }) readonly companyId: string;
    @Prop({ default: "" }) documentId: string;
    @Prop({ default: "" }) zTillReceiptId: string;
    @Prop({ default: "" }) expenseReportId: string;
    @Prop({ default: "" }) bankTransactionId: string;
    // or
    @Prop({ default: undefined }) readonly prefilledRows: Array<any>;
    // new document
    @Prop({ default: "" }) inType: string;
    @Prop({ default: "" }) inLogTagId: string;

    @Prop({ default: null }) actions: { action: DialogAction } | null;
    @Prop() defaultAutoCompletion: boolean;
    @Prop() defaultLog: string;
    @Prop() defaultDate: string;

    // @VModel({ default: null })
    accountingLines: Array<any> = [];

    accountingLinesLocked: boolean = true;

    deleteDocumentLoading = false;

    deleteDocumentDialog = false;

    accountingClosing = undefined;

    onAccountingLinesPage: boolean = window.location.href.includes("accountingLines")
        ? true
        : false;

    type_: string = "MISCELLANEOUS_OPERATION";

    get readonly() {
        if (!this.accountingLinesLocked) {
            return true;
        }
        if (this.accountingClosing) return true;
        return false;
    }

    get isEdition() {
        return false;
    }

    get logs() {
        if (this.bankTransactionId) {
            return [{ value: "BANK_TRANSACTION", text: this.$t("document.type.BANK_TRANSACTION") }];
        }
        if (this.zTillReceiptId) {
            return [{ value: "ZTILL_RECEIPT", text: this.$t("document.type.ZTILL_RECEIPT") }];
        }
        if (this.expenseReportId) {
            return [{ value: "EXPENSE_REPORT", text: this.$t("document.type.EXPENSE_REPORT") }];
        }
        let docLogs = [];
        let allLogs = false;
        if (this.documentId) {
            if (this.accountingLinesLocked) {
                docLogs = [
                    "MISCELLANEOUS_OPERATION",
                    "PURCHASE_INVOICE",
                    "EXPENSE_REPORT",
                    "SALES_INVOICE",
                    "ZTILL_RECEIPT",
                    "PAY_SLIP",
                    "VAT",
                    "CURRENT_ACCOUNT",
                    "OPERATOR_ACCOUNT",
                    "CARRY_FORWARD",
                    "WITH_DELTA",
                ];
            } else {
                docLogs = [
                    "MISCELLANEOUS_OPERATION",
                    "PURCHASE_INVOICE",
                    // "EXPENSE_REPORT",
                    "SALES_INVOICE",
                    // "ZTILL_RECEIPT",
                    "PAY_SLIP",
                    "VAT",
                    "CURRENT_ACCOUNT",
                    "OPERATOR_ACCOUNT",
                    "CARRY_FORWARD",
                    "WITH_DELTA",
                ];
            }
        } else {
            allLogs = true;
            docLogs = [...documentLogs];
            docLogs.push("BANK_TRANSACTION");
        }
        const logs = docLogs.map((x) => ({ value: x, text: this.$t("document.type." + x) }));

        for (const tag of this.allCompanyTags) {
            if (tag.primaryLog) {
                const log = {
                    value: tag.primaryLog + "#" + tag.id,
                    text: tag.text + (tag.count ? " (" + tag.count + ")" : ""),
                    objectType: "tag",
                };
                const index = logs.findIndex((t) => t.value === tag.primaryLog);
                if (index !== -1) {
                    logs.splice(index + 1, 0, log);
                } else if (allLogs) {
                    logs.push(log);
                }
            }
        }
        return logs;
    }

    logTagId: string = "";

    get log() {
        let log = this.type;
        if (this.logTagId) {
            log += "#" + this.logTagId;
        }
        return log;
    }

    set log(log) {
        if (log) {
            const values = log.split("#");
            this.type = values[0];
            if (values.length === 2) {
                this.logTagId = values[1];
            } else {
                this.logTagId = "";
            }
        } else {
            this.type = "";
            this.logTagId = "";
        }
    }

    get type() {
        if (this.bankTransactionId) {
            return "BANK_TRANSACTION";
        }
        return this.type_ || this.defaultLog;
    }

    set type(value) {
        this.type_ = value;
    }

    accountingItemImportId = "";
    accountingItemImport: any = {};

    loadingValidate = false;

    date: string = null;
    lastValidDate: string = null;

    invoiceNumber: string = "";

    generalWording: string = "";

    // emptyCompany = { id:"", name: "", siret: ""}

    setDate(date: string) {
        this.date = date;
        this.lastValidDate = date;
    }

    get emptyRow() {
        return {
            date: moment(this.date).format(),
            accountableAccount: { name: "" },
            // wording: this.generalWording,
            wording: "",
            netAmount: 0,
            debit: 0,
            credit: 0,
            thirdPartyCompany: null,
            user: null,
            bankAccount: null,
            dueDate: null,
            paymentStatus: "",
            stockOperation: null,
        };
    }

    defaultRows = this.onAccountingLinesPage
        ? [JSON.parse(JSON.stringify(this.emptyRow))]
        : [JSON.parse(JSON.stringify(this.emptyRow)), JSON.parse(JSON.stringify(this.emptyRow))];

    get isIncomingOutgoingPayment() {
        if (
            this.selectedCompany?.profile &&
            this.selectedCompany.profile.accountingType === "INCOMING_OUTGOING_PAYMENT"
        ) {
            return true;
        }
        return false;
    }

    get defaultWording() {
        // DECLARATION TVA OCTOBRE 2019
        let wording = this.typeTitle;
        if (this.date) {
            wording += " du " + moment(this.date).format("DD/MM/YYYY");
        }
        return wording.toString();
    }

    get typeTitle() {
        if (this.type && this.$t) {
            return this.$t("document.type." + this.type);
        }
        return "";
    }

    get selectedCompany() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get enableStock() {
        return this.selectedCompany?.profile?.stock;
    }

    get subjectToVAT() {
        return !isNotSubjectToVAT(this.selectedCompany.vatSystem);
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isAccountingUser() {
        return this.currentUser.isAccountingUser;
    }

    get isShortScreen() {
        return this.$vuetify.display.width < 1500;
    }

    get colCount() {
        if (this.showDueDateColumn) {
            return 7;
        }
        return 6;
    }

    get showDueDateColumn() {
        for (const al of this.accountingLines) {
            if (!!al.paymentStatus) {
                return true;
            }
        }
        return false;
    }

    get containsItemWithTag() {
        for (const al of this.accountingLines) {
            if (!!al.tag) {
                return true;
            }
        }
        return false;
    }

    formatDate(date) {
        if (!date) return "";
        return moment(date).format("DD/MM/YYYY");
    }

    getPaymentAATitle(aaName) {
        if (!aaName) {
            return "";
        }
        const dicoAATitles = [
            {
                pattern: new RegExp(/^4456/),
                title: "Montant de la TVA",
            },
            {
                pattern: new RegExp(/^445702/),
                title: "TVA (2.1%)",
            },
            {
                pattern: new RegExp(/^445705/),
                title: "TVA (5.5%)",
            },
            {
                pattern: new RegExp(/^445708/),
                title: "TVA (8.5%)",
            },
            {
                pattern: new RegExp(/^445710/),
                title: "TVA (10.0%)",
            },
            {
                pattern: new RegExp(/^445720/),
                title: "TVA (20.0%)",
            },
            {
                pattern: new RegExp(/^709/),
                title: "Remise",
            },
            {
                pattern: new RegExp(/^[67]/),
                title: "Montant HT",
            },
            {
                pattern: new RegExp(/^2/),
                title: "Immobilisation",
            },
            {
                pattern: new RegExp(/^4[01]1/),
                title: "Montant TTC",
            },
            // {
            //     pattern: new RegExp(/^411/),
            //     title: "Montant TTC"
            // }
        ];

        for (const aaTitle of dicoAATitles) {
            if (aaName.match(aaTitle.pattern)) {
                return aaTitle.title;
            }
        }
        return "";
    }

    getItemAccountableAccountTitle(item) {
        if (item?.accountableAccount) {
            const aa = item.accountableAccount;
            if (!this.isAccountingUser || this.mode === "Payment") {
                return (
                    this.getPaymentAATitle(aa.name) ||
                    aa.shortDescription ||
                    aa.description ||
                    aa.name
                );
            } else {
                let title = aa.displayName || aa.name;
                if (!this.isShortScreen) {
                    title += aa.description ? " - " + aa.description : "";
                } else {
                    title = `${title} - ${this.getThirdPartyName(item)}`;
                }
                // title += aa.description ? (" - " +  aa.description) : ""
                return title;
            }
        }
        return "XXXXXX";
    }

    getThirdPartyName(item) {
        let title = "";
        if (
            item.thirdPartyCompanyToShow && 
            item.thirdPartyCompany
        ) {
            title = item.thirdPartyCompany.name;
        }
        if (
            item.userToShow && 
            item.user
        ) {
            if (title) title += " - ";
            title += getUserDisplayName(item.user);
        }
        if (
            item.bankAccountToShow && 
            item.bankAccount
        ) {
            if (title) title += " - ";
            title += item.bankAccount.name;
        }
        return title;
    }

    /* --- GRAPHQL FUNCTION --- */

    // salesDocumentTypes = ["ZTILL_RECEIPT", "SALES_INVOICE"];

    addDocument(document) {
        if (document.accountingLines && document.accountingLines.length) {
            console.log("addDocument");
            this.setDate(document.date);
            this.generalWording = document.displayName || document.name;
            let sameWording = true;
            let wording = "";
            for (const accountingLine of document.accountingLines) {
                if (
                    accountingLine &&
                    accountingLine.accountableAccount &&
                    accountingLine.accountableAccount.name &&
                    accountingLine.accountableAccount.name.startsWith("SALES")
                ) {
                    continue;
                }
                if (!wording) {
                    wording = accountingLine.wording;
                } else if (sameWording && wording !== accountingLine.wording) {
                    sameWording = false;
                }
                this.addAccountingLine(accountingLine, document.id, undefined, document.status);
            }
            this.accountingLines = this.accountingLines.map((al) => ({
                ...al,
                wording: al.wording === this.generalWording && !this.isStatic ? "" : al.wording,
            }));
            if (sameWording && this.generalWording !== wording) {
                this.generalWording = wording;
            }
        }
    }

    async addAccountingLine(accountingLine, documentId, bankTransactionId, status) {
        if (
            !this.isIncomingOutgoingPayment &&
            accountingLine.type === "MANAGEMENT" &&
            status === "VALIDATED"
        ) {
            return;
        }
        const reconciliationCallToAction = getReconciliationCallToActionTitle(
            this.$store.state.accountableAccountTable,
            accountingLine,
            this.selectedCompany,
            this.isAccountingUser,
        );
        const paymentStatus = this.getPaymentStatus(accountingLine);
        const item = _.cloneDeep({
            ...accountingLine,
            documentId,
            bankTransactionId,
            // thirdPartyCompany: accountingLine.thirdPartyCompany ? accountingLine.thirdPartyCompany.name : "-",
            // accountableAccount: accountingLine.accountableAccount ? accountingLine.accountableAccount.name : "-",
            // wording: accountingLine.wording,
            // date: accountingLine.date,
            debit: accountingLine.amountSigned > 0 ? accountingLine.amountSigned : 0,
            credit: accountingLine.amountSigned < 0 ? -accountingLine.amountSigned : 0,
            reconciliationCallToAction,
            selected: false,
            paymentStatus,
            isBankTransaction:
                bankTransactionId && accountingLine.accountableAccount?.name.startsWith("512"),
            dueDate: paymentStatus ? accountingLine.dueDate || null : null,
        });
        if (item.stockOperation) {
            if (item.stockOperation.productQuantity < 0) {
                item.stockOperation.productQuantity = Math.abs(item.stockOperation.productQuantity);
            }
            if (item.stockOperation.stockOperation?.tag?.id) {
                item.stockOperation.tagId = item.stockOperation.stockOperation.tag.id;
            }
            if (item.stockOperation.stockProduct?.data?.quantities?.length) {
                const quantities = item.stockOperation.stockProduct.data.quantities.map((q) => ({
                    quantityName: q.quantityName,
                    quantity: null,
                }));
                if (item.stockOperation.data?.quantities?.length) {
                    for (let i = 0; i < quantities.length; i++) {
                        if (item.stockOperation.data.quantities[i]?.quantity) {
                            quantities[i].quantity =
                                item.stockOperation.data.quantities[i].quantity;
                        }
                    }
                }
                if (!item.stockOperation.data) {
                    item.stockOperation.data = {};
                }
                item.stockOperation.data.quantities = quantities;
            }
        }

        const toShow: any = await showThirdPartyCompanyOrBankAccountOrUser(item.accountableAccount.id, this.$apollo);
    
        if (toShow && typeof toShow === 'object') {
            item.userToShow = toShow.userToShow || null;
            item.thirdPartyCompanyToShow = toShow.thirdPartyCompanyToShow || null;
            item.bankAccountToShow = toShow.bankAccountToShow || null;
        } else {
            console.error("Invalid structure returned from showThirdPartyCompanyOrBankAccountOrUser:", toShow);
            debugger
        }

        this.getALKey(item);
        this.accountingLines.push(item);

        if (!this.generalWording) {
            this.generalWording = accountingLine.wording;
        }
    }

    get toPayAccountingLineCount() {
        let count = 0;
        for (const al of this.accountingLines) {
            if (!al.tag && al.paymentStatus === "TODO") {
                count++;
            }
        }
        return count;
    }

    get dueDate() {
        for (const al of this.accountingLines) {
            if (al.paymentStatus && al.documentId) {
                if (!al.dueDate) {
                    al.dueDate = al.date;
                }
                return al.dueDate;
            }
        }
        return null;
    }

    set dueDate(date) {
        for (const al of this.accountingLines) {
            if (al.paymentStatus && al.documentId) {
                al.dueDate = date;
            }
        }
    }

    get candidateForPaymentAccountingLineCount() {
        let count = 0;
        for (const al of this.accountingLines) {
            if (al.paymentStatus) {
                count++;
            }
        }
        return count;
    }

    get selectedAccountingLines() {
        return this.accountingLines.filter((al) => al.selected);
    }

    get isStatic() {
        return false;
    }

    getALKey(al) {
        if (!al.key) {
            al.key = al.accountableAccount?.name || "XXXXXX";
            if (
                al.thirdPartyCompanyToShow && 
                al.thirdPartyCompany
            ) {
                al.key += "-" + al.thirdPartyCompany.id;
            }
            if (
                al.userToShow && 
                al.user
            ) {
                al.key += "-" + al.user.id;
            }
            if (
                al.bankAccountToShow && 
                al.bankAccount
            ) {
                al.key += "-" + al.bankAccount.id;
            }
        }
        return al.key;
    }

    get selectedAccountingLinesForPayment() {
        let firstALKey;
        const als = [];
        for (const al of this.selectedAccountingLines) {
            if (al.paymentStatus !== "TODO") {
                return [];
            }
            const key = this.getALKey(al);
            if (!firstALKey) {
                firstALKey = key;
                als.push(al);
            } else if (key === firstALKey) {
                als.push(al);
            } else {
                // return []
                als.push(al);
            }
        }
        return als;
    }

    getPaymentStatus(accountingLine) {
        if (!accountingLine?.accountableAccount?.name) {
            return "";
        }
        const aaName = accountingLine.accountableAccount.name;
        if (this.$store.state.accountableAccountTable) {
            const dbAA = this.$store.state.accountableAccountTable.accountableAccounts.find(
                (aa) => aa.name === aaName,
            );
            if (dbAA?.lettering) {
                const excludedForPaymentAccountableAccounts = [/^589999/i];
                for (const excludedForPaymentAccountableAccount of excludedForPaymentAccountableAccounts) {
                    if (aaName.match(excludedForPaymentAccountableAccount)) {
                        return "";
                    }
                }

                if (accountingLine.reconciliation) {
                    return "DONE";
                }
                return "TODO";
            }
        }
        return "";
    }

    getPaymentStatusTitle(accountingLine) {
        if (accountingLine.paymentStatus === "TODO") {
            return "A régler";
        }
        if (accountingLine.paymentStatus === "DONE") {
            return "Réglé";
        }

        return "";
    }

    get detailHeaders() {
        const headers = [];
        if (this.candidateForPaymentAccountingLineCount) {
            headers.push({ text: "", value: "", sortable: false, align: "center" });
        }

        headers.push({ text: "Affectation", value: "wording", sortable: false, align: "start" });

        if (this.showDueDateColumn) {
            headers.push({ text: "Échéance", value: "dueDate", sortable: false, align: "center" });
        }

        headers.push({ text: "Montant", value: "amount", sortable: false, align: "end" });

        if (this.candidateForPaymentAccountingLineCount) {
            headers.push({ text: "Statut", value: "status", sortable: false, align: "center" });
        }
        return headers;
    }

    getAccountableAccountForEdition(item) {
        if (item.stockOperation?.stockProduct?.id) {
            return {
                ...item.accountableAccount,
                invoiceProduct: {
                    id: item.stockOperation.stockProduct.id,
                },
            };
        }
        return item.accountableAccount;
    }

    openReconciliation(item) {
        if (item) {
            const params: any = {
                companyId: this.companyId,
                accountingLineId: item.id,
                documentId: item.documentId,
            };
            if (this.mode !== "GeneralLedger") {
                this.$router.push({
                    name: "reconciliation/accountingLine",
                    params,
                });
            } else {
                this.$emit("openReconciliation", params);
            }
        }
    }

    async initialize() {
        if (this.inType && this.logs.find((l) => l.value === this.inType)) {
            this.type = this.inType;
        }
        this.loadingValidate = false;
        this.accountingLines = this.prefilledRows?.length
            ? JSON.parse(JSON.stringify(this.prefilledRows))
            : JSON.parse(JSON.stringify(this.defaultRows));

        if (this.prefilledRows?.length) {
            if (this.prefilledRows[0].date) {
                this.setDate(this.prefilledRows[0].date);
            }
            if (this.prefilledRows[0].wording) {
                this.generalWording = this.prefilledRows[0].wording;
            }
        } else {
            this.generalWording = this.defaultWording;
        }
    }

    addEmptyAL(aa) {
        const al = _.cloneDeep(this.emptyRow);
        if (aa) {
            al.accountableAccount = aa;
            this.accountingLines.push(al);
        }
    }

    beforeMount() {
        if (this.defaultLog) {
            this.type_ = "";
        }
        if (this.inLogTagId) {
            this.logTagId = this.inLogTagId;
        }
        //
        // check date
        //
        this.computeDate();
        for (const row of this.defaultRows) {
            row.date = moment(this.date).format();
        }
        this.refresh();
    }

    computeDate() {
        console.log("computeDate className=", this.className);
        if (
            this.documentId ||
            this.zTillReceiptId ||
            this.expenseReportId ||
            this.bankTransactionId
        ) {
            return;
        }
        if (this.defaultDate) {
            this.setDate(this.defaultDate);
            return;
        }
        const date: string = moment().startOf("month").add(-1, "days").format();
        const {
            accountingPeriodWasNull: accPeriodIsNull,
            accountingPeriods,
            selectedAccountingPeriod: accountingPeriod,
        } = this.$store.state.accountingPeriod;
        const today = moment();
        const todayIsInAccountingPeriod = accountingPeriod
            ? today.isSameOrAfter(moment(accountingPeriod.start)) &&
              today.isSameOrBefore(moment(accountingPeriod.end)) &&
              !accountingPeriod.closed
            : null;
        if (accPeriodIsNull || todayIsInAccountingPeriod) {
            this.setDate(getAccountingDate(this.$store, date));
            return;
        }

        if (accountingPeriod.closed) {
            const { firstUnclosedPeriod } =
                getLastClosedAndFirstOpenAccountingPeriods(accountingPeriods);
            this.setDate(moment(firstUnclosedPeriod.start).format());
            return;
        }

        this.setDate(moment(accountingPeriod.end).format());
    }

    async refresh() {
        this.generalWording = "";
        this.accountingLines = []; // reset
        if (this.documentId) {
            const res: any = await this.$apollo.query({
                query: DOCUMENT_STATIC_QUERY,
                fetchPolicy: "network-only",
                variables: {
                    documentId: this.documentId,
                },
            });
            if (res && res.data && res.data.document) {
                this.type = res.data.document.type;
                this.invoiceNumber = res.data.document.invoiceNumber;
                this.accountingLinesLocked = res.data.document.accountingLinesLocked;
                this.accountingItemImportId = res.data.document.accountingItemImport
                    ? res.data.document.accountingItemImport.id
                    : undefined;
                this.accountingItemImport = res.data.document.accountingItemImport || undefined;
                this.accountingClosing = res.data.document.accountingClosing;
                this.addDocument(res.data.document);
                if (!this.isEdition) {
                    if (res.data.document.fromDocumentOf?.length) {
                        for (const childDoc of res.data.document.fromDocumentOf) {
                            if (childDoc.status !== "DELETED") {
                                this.addDocument(childDoc);
                            }
                        }
                    }
                }
            }
        } else if (this.zTillReceiptId) {
            this.type = "ZTILL_RECEIPT";
            const res: any = await this.$apollo.query({
                query: ZTILL_RECEIPT_STATIC_QUERY,
                fetchPolicy: "network-only",
                variables: {
                    documentId: this.zTillReceiptId,
                },
            });
            if (res && res.data && res.data.zTillReceipt && res.data.zTillReceipt.document) {
                if (res.data.zTillReceipt.document) {
                    this.accountingItemImportId = res.data.zTillReceipt.accountingItemImport
                        ? res.data.zTillReceipt.accountingItemImport.id
                        : undefined;
                    this.invoiceNumber = res.data.zTillReceipt.document.invoiceNumber;
                    this.accountingClosing = res.data.zTillReceipt.document.accountingClosing;
                    this.addDocument(res.data.zTillReceipt.document);
                }
            } else if (
                res &&
                res.data &&
                res.data.zTillReceipt &&
                res.data.zTillReceipt.documents
            ) {
                this.accountingItemImportId = res.data.zTillReceipt.accountingItemImport
                    ? res.data.zTillReceipt.accountingItemImport.id
                    : undefined;
                for (const doc of res.data.zTillReceipt.documents) {
                    this.addDocument(doc);
                }
            }
        } else if (this.expenseReportId) {
            const res: any = await this.$apollo.query({
                query: EXPENSE_REPORT_STATIC_QUERY,
                fetchPolicy: "network-only",
                variables: {
                    id: this.expenseReportId,
                },
            });
            if (res && res.data && res.data.expenseReport) {
                this.type = "EXPENSE_REPORT";
                const expenseReport = res.data.expenseReport;
                this.accountingItemImportId = expenseReport.accountingItemImport
                    ? expenseReport.accountingItemImport.id
                    : undefined;

                if (expenseReport.expenseReport) {
                    this.invoiceNumber = expenseReport.expenseReport.invoiceNumber;
                    this.accountingClosing = expenseReport.expenseReport.accountingClosing;
                    this.addDocument(expenseReport.expenseReport);
                }
            }
        } else if (this.bankTransactionId) {
            const res: any = await this.$apollo.query({
                query: BANK_TRANSACTION_ACCOUNTING_LINES_QUERY,
                fetchPolicy: "network-only",
                variables: {
                    bankTransactionId: this.bankTransactionId,
                },
            });
            if (res && res.data && res.data.bankTransaction) {
                const bankTransaction = res.data.bankTransaction;
                this.type = "BANK_TRANSACTION";
                this.accountingItemImportId = res.data.bankTransaction.accountingItemImport
                    ? res.data.bankTransaction.accountingItemImport.id
                    : undefined;
                this.accountingItemImport =
                    res.data.bankTransaction.accountingItemImport || undefined;
                this.accountingClosing = res.data.bankTransaction.accountingClosing;

                this.setDate(bankTransaction.dateDebit);
                this.generalWording =
                    bankTransaction.wordingDisplay || bankTransaction.wordingOriginal;
                const notJustifiedAmountSigned = computeNotJustifiedAmountSigned(
                    bankTransaction,
                    bankTransaction.accountingLines,
                );
                if (!bankTransaction.version || bankTransaction.version === 1) {
                    this.addAccountingLine(
                        {
                            date: bankTransaction.dateDebit,
                            amountSigned: bankTransaction.amount,
                            accountableAccount: {
                                name: "512000",
                                description: "Banques",
                            },
                            wording: this.generalWording,
                            bankAccount: {
                                id: bankTransaction.account.id,
                                name: bankTransaction.account.name,
                            },
                            isBankTransaction: true,
                        },
                        undefined,
                        bankTransaction.id,
                        "VALIDATED",
                    );
                }
                for (const accountingLine of bankTransaction.accountingLines) {
                    this.addAccountingLine(
                        accountingLine,
                        undefined,
                        bankTransaction.id,
                        "VALIDATED",
                    );
                }
                if (Math.abs(notJustifiedAmountSigned) >= 1e-2) {
                    this.addAccountingLine(
                        {
                            date: bankTransaction.dateDebit,
                            amountSigned: mathFix(-notJustifiedAmountSigned),
                            accountableAccount: {
                                name: "471000",
                                description: "Comptes d'attente",
                            },
                            wording: this.generalWording,
                        },
                        undefined,
                        bankTransaction.id,
                        "VALIDATED",
                    );
                }
            }
        } else {
            this.initialize();
        }
        if (!this.generalWording) {
            this.generalWording = this.defaultWording;
        }
        // if (this.isEdition && !this.accountingLines.length) {
        //     this.initializeALs()
        // }

        //
        //
        //
        if (this.mode === "Payment") {
            let uniqueToPayAL;
            for (const al of this.accountingLines) {
                if (al.paymentStatus === "TODO") {
                    if (!uniqueToPayAL) {
                        uniqueToPayAL = al;
                    } else {
                        uniqueToPayAL = undefined;
                        break;
                    }
                }
            }
            if (uniqueToPayAL) {
                uniqueToPayAL.selected = true;
            }
        }
        // this.$emit('refresh')
    }

    get isNew() {
        if (this.documentId) {
            return false;
        }
        if (this.bankTransactionId) {
            return false;
        }
        if (this.zTillReceiptId) {
            return false;
        }
        if (this.expenseReportId) {
            return false;
        }
        return true;
    }

    async deleteDocument() {
        this.deleteDocumentLoading = true;

        await this.$apollo.mutate({
            mutation: DELETE_DOCUMENT,
            variables: {
                documentId: this.documentId,
            },
        });

        this.deleteDocumentDialog = false;
        this.deleteDocumentLoading = false;

        this.$router.go(-1);
    }

    initialValue = "";

    onEdit(item, att) {
        this.initialValue = item[att];
    }
}
